import { useState, useEffect } from 'react';
import { getFileExtension } from '../../../artifact/utils';
import useDocumentPreviewService from '../../documentPreviewService';
import { useArtifactDownloadHelper } from 'domain/shared/artifact';
import { useMessageService, ErrorMessage } from 'framework/ui/Message';
import { DEFAULT_ERROR_MESSAGE } from 'framework/form';
import useDocumentBlob from '../useDocumentBlob';

interface UseDocumentPreviewProps {
  previewPath: string | null; // path for preview (render)
  filePath: string | null; // original path for downloading and printing
  onClose?: () => void;
}

const useDocumentPreview = ({ previewPath, filePath, onClose }: UseDocumentPreviewProps) => {
  const [fileExtension, setFileExtension] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1.2);

  const { downloadDocumentStorage } = useArtifactDownloadHelper();
  const { isPreviewAvailable, isPreviewImage } = useDocumentPreviewService();
  const { addMessage } = useMessageService();

  // previewPath is used only for loadBlob (render preview)
  const { blobUrl, loading, hasError, loadBlob } = useDocumentBlob(null);

  const loadPreview = async () => {
    const resultPath = previewPath || filePath;

    if (!resultPath) return;

    const fileExt = getFileExtension(resultPath)!;
    setFileExtension(fileExt);

    try {
      if (!isPreviewAvailable(fileExt) && !isPreviewImage(fileExt)) {
        if (filePath) await downloadDocumentStorage(filePath);
        setOpen(false);
        return;
      }
      loadBlob(resultPath);
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const handleDownload = async () => {
    if (!filePath) return;
    try {
      await downloadDocumentStorage(filePath);
    } catch {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  const handlePrint = () => {
    if (!blobUrl) return;
    const printWindow = window.open(blobUrl);
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    }
  };

  const zoomIn = () => setScale(s => Math.min(s + 0.2, 3));
  const zoomOut = () => setScale(s => Math.max(s - 0.2, 0.6));

  const onDocumentLoadSuccess = ({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages);
  };

  useEffect(() => {
    if (previewPath) {
      setOpen(true);
      loadPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewPath, filePath]);

  useEffect(() => {
    if (hasError) {
      addMessage(new ErrorMessage(DEFAULT_ERROR_MESSAGE));
      setOpen(false);
      if (filePath) {
        handleDownload();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  return {
    open,
    loading,
    blobUrl,
    fileExtension,
    isPreviewAvailable,
    isPreviewImage,
    handleDownload,
    handlePrint,
    handleClose,
    numPages,
    onDocumentLoadSuccess,
    scale,
    zoomIn,
    zoomOut,
    hasError,
  };
};

export default useDocumentPreview;
